import React from 'react';
import { Notification, toaster } from '@appkit4/react-components/notification';
import {
    getChatMessages,
    getMessagesBySessionId,
    terminateGeneration,
    getGenerationStatus,
    createChatSession,
} from '@config/getSessionApis';
import { ModificationOptions } from '@contexts/SessionProvider';
import {
    getCodeFiles,
    tryToCompleteFinalJSON,
    tryToCompleteJSON,
    responseMessageParser,
    CurrentSource,
    SourcePayload,
    preprocessSessionMessages,
} from './shared';

export const handleSuccessResponse = async (
    response: any,
    index: string,
    setStreamMessageProvider: React.Dispatch<React.SetStateAction<any>>,
    successfulResponses: any[],
    setCurrentSource: React.Dispatch<React.SetStateAction<CurrentSource>>,
    _currentGenerationId: string,
    setCurrentGenerationId: React.Dispatch<React.SetStateAction<string>>,
    setIsGenerateComplete: React.Dispatch<React.SetStateAction<boolean>>,
    setShowCodeStream: React.Dispatch<React.SetStateAction<boolean>>,
    setStreamMessagesConsumer: React.Dispatch<React.SetStateAction<string[]>>,
    sessionId: string,
    userEmail: string,
    setCurrentChatSessionId: React.Dispatch<React.SetStateAction<number>>,
    setHistory: React.Dispatch<React.SetStateAction<any[]>>,
    setThread: React.Dispatch<React.SetStateAction<any[]>>,
    setCurrentHistoryCommit: React.Dispatch<React.SetStateAction<string>>,
    isFirst: boolean,
    setToolStatus: React.Dispatch<React.SetStateAction<string | null>>,
    setGenerationStatus: React.Dispatch<React.SetStateAction<string | null>>,
    setPostProcessingFailed: React.Dispatch<React.SetStateAction<boolean>>
) => {
    let stoppedByUser = false;

    // Poll the backend until post-processing completes, fails, or times out (60s).
    // Returns a cancel function to prevent stale callbacks after unmount.
    const pollPostProcessingStatus = (id: string): (() => void) => {
        let cancelled = false;
        const POLL_INTERVAL_MS = 2000;
        const POLL_TIMEOUT_MS = 60000;
        const startedAt = Date.now();

        const poll = async () => {
            if (cancelled) return;
            if (Date.now() - startedAt > POLL_TIMEOUT_MS) {
                console.warn('[GenerateHandler] Post-processing status poll timed out for', id);
                return;
            }
            const result = await getGenerationStatus(id);
            if (cancelled) return;
            if (!result) {
                // Network error — retry
                setTimeout(poll, POLL_INTERVAL_MS);
                return;
            }
            if (result.status === 'post_processing_complete') {
                return;
            }
            if (result.status === 'post_processing_failed') {
                console.error('[GenerateHandler] Post-processing failed for generation', id, result.error);
                setPostProcessingFailed(true);
                toaster.notify(
                    React.createElement(Notification, {
                        status: 'warning',
                        message: 'Your code was generated but could not be saved. Please try regenerating.',
                        closeable: true,
                    })
                );
                return;
            }
            // Still running (agentic_stream_done / running) — keep polling
            setTimeout(poll, POLL_INTERVAL_MS);
        };

        setTimeout(poll, POLL_INTERVAL_MS);
        return () => {
            cancelled = true;
        };
    };

    const terminateStream = async (generationId: string, userStopped = false) => {
        if (userStopped) stoppedByUser = true;
        clearStatusQueue();
        await terminateGeneration(generationId);
        setStreamMessagesConsumer([]);
        setStreamMessageProvider([]);

        if (!userStopped && !stoppedByUser) {
            const response = await getMessagesBySessionId(userEmail, sessionId);
            const historialMessagesResult = response.resultObject;
            if (historialMessagesResult && historialMessagesResult.length) {
                const activeSession = preprocessSessionMessages(historialMessagesResult);
                if (activeSession && activeSession[0] && activeSession[0][0]) {
                    setCurrentChatSessionId(activeSession[0][0].chatSessionId);
                }
                setHistory(activeSession);
                setThread(activeSession);
            }
        }

        setIsGenerateComplete(true);
        setToolStatus(null);
        setGenerationStatus(null);
        setShowCodeStream(false);
    };
    const reader = response.body.getReader();
    const decoder = new TextDecoder('utf-8');
    let done = false;
    let finalContent = '';
    let fileResponse = '';
    let lastFiles = {};
    let partialLine = '';

    const fileStack: any[] = [];

    let progressiveIntervalId: any;
    let generationId = '';
    let finalReceived = false;

    // Accumulates file_chunk content for the agentic structured-event path
    let agenticContent = '';

    // Format a tool_call event into a human-readable status message
    const formatToolCallMessage = (event: { name: string; args?: any }): string => {
        switch (event.name) {
            case 'read_file':
                return `Reading ${event.args?.filePath || 'file'}...`;
            case 'search_project_files':
                return `Searching for '${event.args?.query || 'files'}'...`;
            case 'list_project_structure':
                return `Exploring ${event.args?.directory || 'project'} structure...`;
            case 'get_design_system_docs':
                return `Loading ${event.args?.componentName || 'component'} docs...`;
            case 'remove_file':
                return `Removing ${event.args?.filePath || 'file'}...`;
            default:
                return `Running ${event.name}...`;
        }
    };

    // Status message queue — drip-feed messages so batched chunks don't clobber each other
    const statusQueue: string[] = [];
    let statusTimerId: ReturnType<typeof setTimeout> | null = null;
    let statusDraining = false;

    const drainStatusQueue = () => {
        if (statusQueue.length === 0) {
            statusDraining = false;
            return;
        }
        statusDraining = true;
        const next = statusQueue.shift()!;
        setToolStatus(next);
        statusTimerId = setTimeout(drainStatusQueue, 600);
    };

    const enqueueStatus = (msg: string) => {
        // If same message is already the last in queue, skip duplicate
        if (statusQueue.length > 0 && statusQueue[statusQueue.length - 1] === msg) {
            return;
        }
        // When a specific tool message arrives, drop queued generic filler messages
        // that haven't been displayed yet (they'd just flash by uselessly)
        const isGeneric =
            msg === 'Analyzing your project...' || msg === 'Processing results...' || msg === 'Generating code...';
        if (!isGeneric && statusQueue.length > 0) {
            // Remove trailing generic messages from queue — the specific message supersedes them
            while (
                statusQueue.length > 0 &&
                (statusQueue[statusQueue.length - 1] === 'Analyzing your project...' ||
                    statusQueue[statusQueue.length - 1] === 'Processing results...' ||
                    statusQueue[statusQueue.length - 1] === 'Generating code...')
            ) {
                statusQueue.pop();
            }
        }
        statusQueue.push(msg);
        if (!statusDraining) {
            drainStatusQueue();
        }
    };

    const clearStatusQueue = () => {
        statusQueue.length = 0;
        if (statusTimerId) {
            clearTimeout(statusTimerId);
            statusTimerId = null;
        }
        statusDraining = false;
    };
    let userStopped = false;
    while (!done) {
        let readResult;
        try {
            readResult = await reader.read();
        } catch (readError: any) {
            if (readError.name === 'AbortError') {
                console.info('Stream aborted by client');
                userStopped = true;
                clearInterval(progressiveIntervalId);
                clearStatusQueue();
                break;
            }
            // If we already received the final payload, the stream read error is benign
            // (e.g., PrematureCloseException from backend after $RC-FINAL$ was delivered).
            if (finalReceived) {
                console.warn('[GenerateHandler] Stream read error after $RC-FINAL$ received, ignoring:', readError);
                break;
            }
            throw readError;
        }
        const { value, done: doneReading } = readResult;
        done = doneReading;
        let chunkStr = decoder.decode(value, { stream: true });
        if (isFirst) {
            progressiveIntervalId = setInterval(() => {
                if (fileStack.length > 0) {
                    if (JSON.stringify(lastFiles) === JSON.stringify(fileStack[fileStack.length - 1])) {
                        return;
                    }
                    const latestItem = fileStack[fileStack.length - 1];
                    lastFiles = latestItem;
                    if (!done) {
                        setCurrentSource(latestItem);
                    }
                }
            }, 5000);
        }

        // Combine leftover partial data with new chunk
        chunkStr = partialLine + chunkStr;
        partialLine = '';

        // Split lines and store the last incomplete line
        const lines = chunkStr?.split('\n');
        partialLine = lines.pop() || '';

        // Process complete lines only
        const parsedLines = lines
            .map((line) => {
                return line
                    .replace(/^data:/, '')
                    .replace(/^data:/, '')
                    .trim();
            })
            .filter((line) => line && line !== '')
            .map((line) => {
                if (line.startsWith('$RC-INIT$:')) {
                    generationId = line.replace('$RC-INIT$:', '').trim();
                    setCurrentGenerationId(generationId);
                    return {};
                }
                if (line.startsWith('$RC-FINAL$')) {
                    fileResponse = line.replace('$RC-FINAL$: ', '');
                    finalReceived = true;
                    terminateStream(generationId);
                    pollPostProcessingStatus(generationId);
                    done = true;
                    return {};
                }
                if (line.startsWith('$RC-HEARTBEAT$')) {
                    return {};
                }
                if (line.startsWith('$RC-ERROR$')) {
                    const errorPayload = line.replace('$RC-ERROR$: ', '').trim();
                    try {
                        const errorData = JSON.parse(errorPayload);
                        console.error('[GenerateHandler] Stream error received:', errorData);
                        if (errorData.code === 'VALIDATION_ERROR') {
                            toaster.notify(
                                React.createElement(Notification, {
                                    status: 'error',
                                    title: 'File Too Large',
                                    message:
                                        'The uploaded file is too large to process. Please reduce the number of rows or simplify your prompt and try again.',
                                    closeable: true,
                                })
                            );
                        }
                    } catch {
                        console.error('[GenerateHandler] Stream error (unparseable):', errorPayload);
                    }
                    done = true;
                    terminateStream(generationId);
                    return {};
                }
                if (line.startsWith('$RC-STOPPED$')) {
                    console.info('Generation terminated by user');
                    userStopped = true;
                    done = true;
                    clearInterval(progressiveIntervalId);
                    terminateStream(generationId, true);
                    return {};
                }
                if (line.startsWith('$RC-STATUS$:')) {
                    const statusMessage = line.replace('$RC-STATUS$:', '').trim();
                    if (statusMessage) {
                        enqueueStatus(statusMessage);
                    }
                    return {};
                }
                if (line === '[DONE]') {
                    return {};
                }
                try {
                    // Check if line is HTML instead of JSON
                    if (line.trim().startsWith('<!DOCTYPE') || line.trim().startsWith('<html')) {
                        //console.error('Stream received HTML instead of JSON. Server error detected.');
                        //console.error('HTML preview:', line.substring(0, 100));
                        return {};
                    }
                    const parsed = JSON.parse(line);

                    // Handle structured events from the agentic path
                    if (parsed.type) {
                        switch (parsed.type) {
                            case 'status':
                                if (parsed.message) enqueueStatus(parsed.message);
                                break;
                            case 'tool_call':
                                enqueueStatus(formatToolCallMessage(parsed));
                                break;
                            case 'tool_result':
                                // Optional: could show "Done reading file.ts" but skip for now
                                break;
                            case 'file_start':
                                if (parsed.path) setGenerationStatus(`Generating ${parsed.path}...`);
                                break;
                            case 'file_chunk':
                                // Accumulate content for progressive preview
                                if (parsed.content) {
                                    agenticContent += parsed.content;
                                    const parsedChunk = tryToCompleteJSON(agenticContent);
                                    const progressiveSource = responseMessageParser(parsedChunk)?.progressiveSource;
                                    if (progressiveSource) {
                                        fileStack.push({ payload: { files: progressiveSource } });
                                    }
                                    const nextMessages = responseMessageParser(parsedChunk)?.nextMessages;
                                    if (nextMessages) {
                                        setStreamMessageProvider?.(nextMessages);
                                    }
                                }
                                break;
                            case 'file_end':
                                // File complete, generation status will update on next file_start
                                break;
                            case 'complete':
                                if (parsed.message) {
                                    // Truncate long backend messages so the overlay shows a concise status
                                    const msg =
                                        parsed.message.length > 80
                                            ? parsed.message.slice(0, 77) + '...'
                                            : parsed.message;
                                    setGenerationStatus(msg);
                                }
                                break;
                        }
                        return {};
                    }

                    return parsed;
                } catch (err) {
                    console.debug('Stream chunk JSON parse error (partial line, will retry):', err);
                    //console.error('Failed line preview:', line.substring(0, 100));
                    return {};
                }
            })
            .filter((item) => {
                return item && item.choices && item.choices[0];
            });
        parsedLines.forEach((line) => {
            const content = line.choices[0].delta.content;
            finalContent += content || '';
            const parsedChunk = tryToCompleteJSON(finalContent);
            const nextMessages = responseMessageParser(parsedChunk)?.nextMessages;
            const progressiveSource = responseMessageParser(parsedChunk)?.progressiveSource;

            if (progressiveSource) {
                const nextCurrentSource = {
                    payload: { files: progressiveSource },
                };
                // Push the latest progressive source to the stack
                fileStack.push(nextCurrentSource);
            }

            if (nextMessages) {
                setStreamMessageProvider?.(nextMessages);
            }
        });
    }
    // Flush any remaining data in partialLine (e.g. $RC-FINAL$ without trailing \n)
    if (partialLine && !finalReceived) {
        const remainingLine = partialLine
            .replace(/^data:/, '')
            .replace(/^data:/, '')
            .trim();
        if (remainingLine.startsWith('$RC-FINAL$')) {
            fileResponse = remainingLine.replace('$RC-FINAL$: ', '');
            finalReceived = true;
            terminateStream(generationId);
            pollPostProcessingStatus(generationId);
        }
        // Also handle $RC-ERROR$ that may arrive without trailing newline
        if (remainingLine.startsWith('$RC-ERROR$')) {
            const errorPayload = remainingLine.replace('$RC-ERROR$: ', '').trim();
            try {
                const errorData = JSON.parse(errorPayload);
                console.error('[GenerateHandler] Stream error received (flush):', errorData);
                if (errorData.code === 'VALIDATION_ERROR') {
                    toaster.notify(
                        React.createElement(Notification, {
                            status: 'error',
                            title: 'File Too Large',
                            message:
                                'The uploaded file is too large to process. Please reduce the number of rows or simplify your prompt and try again.',
                            closeable: true,
                        })
                    );
                }
            } catch {
                console.error('[GenerateHandler] Stream error (unparseable, flush):', errorPayload);
            }
        }
    }
    if (userStopped) {
        return;
    }
    const effectiveFinalContent = finalContent || agenticContent;
    const parsedFinalContentString = tryToCompleteFinalJSON(effectiveFinalContent);
    const nextFinalMessages = responseMessageParser(parsedFinalContentString)?.nextMessages;
    if (nextFinalMessages) {
        nextFinalMessages.push('Code loading finished');
        setStreamMessageProvider?.(nextFinalMessages);
    }
    let parsedFileResponse: any = {};
    try {
        // Check if response is HTML (error page) instead of JSON
        if (fileResponse.trim().startsWith('<!DOCTYPE') || fileResponse.trim().startsWith('<html')) {
            //console.error('RC final response is HTML, not JSON. Server returned an error page.');
            //console.error('Response preview:', fileResponse.substring(0, 200));
            parsedFileResponse = { success: false, error: 'Server returned HTML error page' };
        } else {
            parsedFileResponse = JSON.parse(fileResponse);
        }
    } catch (err) {
        console.error('RC final file response parse failed:', err);
        //console.error('Response that failed to parse:', fileResponse.substring(0, 200));
    }
    let source: SourcePayload;
    if (parsedFileResponse.success) {
        source = getCodeFiles(parsedFileResponse.completion.files);
        successfulResponses.push({
            option: index,
            payload: source,
            commitId: parsedFileResponse.completion.commitId,
            chatSessionId: parsedFileResponse.completion.chatSessionId,
            message: parsedFileResponse.completion.message,
        });
        const updatedSources = [...successfulResponses];
        if (updatedSources.length === 1) {
            fileStack.push({
                payload: source,
                commitId: parsedFileResponse.completion.commitId,
                chatSessionId: parsedFileResponse.completion.chatSessionId,
            });
            setCurrentSource({
                payload: { files: parsedFileResponse.completion.files },
            });
            setCurrentHistoryCommit(parsedFileResponse.completion.commitId);
        }
        clearInterval(progressiveIntervalId);
    }
};

// Helper function to extract actual File object from AppKit upload wrapper or return File directly
const extractFile = (fileOrWrapper: any): File | null => {
    if (!fileOrWrapper) return null;

    // If it's already a File object, return it
    if (fileOrWrapper instanceof File) {
        return fileOrWrapper;
    }

    // If it's an AppKit upload wrapper with originFile property
    if (fileOrWrapper.originFile && fileOrWrapper.originFile instanceof File) {
        return fileOrWrapper.originFile;
    }

    // If it has file-like properties but no originFile, it might be a different wrapper format
    if (fileOrWrapper.name && fileOrWrapper.size && fileOrWrapper.type) {
        console.warn('File wrapper detected but no originFile found:', fileOrWrapper);
        return null;
    }

    return null;
};

export const createPayload = async ({
    chatSessionId,
    userEmail,
    sessionId,
    prompt,
    designSystem,
    templateVersion,
    attachFile,
    attachDataFile,
    processedImage,
    processedDataToon,
    processedDataFileName,
    commitId,
    modificationOptions,
    setCurrentChatSessionId,
}: {
    chatSessionId?: number;
    userEmail: string;
    sessionId: string;
    prompt: string;
    designSystem: string;
    templateVersion: string;
    attachFile: File | null;
    attachDataFile?: File | null;
    processedImage?: Blob | null;
    processedDataToon?: string | null;
    processedDataFileName?: string | null;
    commitId?: string;
    modificationOptions: ModificationOptions;
    setCurrentChatSessionId: any;
}) => {
    const isFollowUp = chatSessionId !== undefined && chatSessionId !== null;
    if (isFollowUp && !commitId) {
        const error = new Error('We are experiencing a temporary issue. Please try again in 2 minutes.');
        (error as any).code = 'DDMPC001';
        (error as any).details = 'Project context lost - follow-up request missing commitId in createPayload';
        console.error('[DDMPC001] Follow-up request missing commitId - context may be lost!');
        throw error;
    }

    const payload = new FormData();
    let localChatSessionId = -1;
    if (!chatSessionId) {
        const chatSessionResult = await createChatSession(userEmail, sessionId, templateVersion);
        localChatSessionId = chatSessionResult.resultObject.chatSessionId;
    } else {
        localChatSessionId = chatSessionId;
    }
    setCurrentChatSessionId(localChatSessionId);

    const timestamp = Date.now().toString();
    const isFirstChat = chatSessionId ? 'false' : 'true';

    payload.append('chatSessionId', String(localChatSessionId));
    payload.append('email', userEmail);
    payload.append('timestamp', timestamp);
    payload.append('prompt', prompt);
    payload.append('firstChat', isFirstChat);
    payload.append('designSystem', designSystem);
    payload.append('templateVersion', templateVersion);

    if (commitId) {
        payload.append('commitId', commitId);
    }

    if (attachFile) {
        if (processedImage) {
            // Use backend-processed image (resized PNG blob)
            const processedFile = new File([processedImage], attachFile.name, { type: 'image/png' });
            payload.append('file', processedFile);
        } else {
            // Fallback: send raw file if no processed result available
            const actualFile = extractFile(attachFile);
            if (actualFile) {
                payload.append('file', actualFile);
            } else {
                console.error('Could not extract valid File object from attachFile:', attachFile);
            }
        }
    }

    if (processedDataToon && processedDataFileName) {
        // Use backend-processed TOON data
        payload.append('dataFileFormat', 'toon');
        payload.append('dataFileContent', processedDataToon);
        payload.append('dataFileName', processedDataFileName);
    } else if (attachDataFile) {
        // Fallback: send raw data file if no processed result available
        const actualDataFile = extractFile(attachDataFile);
        if (actualDataFile) {
            payload.append('dataFile', actualDataFile);
        } else {
            console.error('Could not extract valid File object from attachDataFile:', attachDataFile);
        }
    }

    if (modificationOptions) {
        Object.keys(modificationOptions).forEach((key) => {
            const typedKey = key as keyof ModificationOptions;
            payload.append(key, modificationOptions[typedKey]);
        });
    }

    return payload;
};

export const handleGenerate = async (
    prompt: string,
    userEmail: string,
    attachFile: File | null,
    attachDataFile: File | null,
    modificationOptions: ModificationOptions,
    projectCount: number,
    designSystem: string,
    templateVersion: string,
    sessionId: string,
    currentGenerationId: string,
    setCurrentGenerationId: React.Dispatch<React.SetStateAction<string>>,
    setCurrentSource: React.Dispatch<React.SetStateAction<CurrentSource>>,
    setCurrentChatSessionId: React.Dispatch<React.SetStateAction<number>>,
    setStreamMessageProvider: React.Dispatch<React.SetStateAction<any>>,
    setIsGenerateComplete: React.Dispatch<React.SetStateAction<boolean>>,
    setShowCodeStream: React.Dispatch<React.SetStateAction<boolean>>,
    setStreamMessagesConsumer: React.Dispatch<React.SetStateAction<string[]>>,
    setHistory: React.Dispatch<React.SetStateAction<any[]>>,
    setThread: React.Dispatch<React.SetStateAction<any[]>>,
    setCurrentHistoryCommit: React.Dispatch<React.SetStateAction<string>>,
    setGenerationError: React.Dispatch<React.SetStateAction<any>>,
    setToolStatus: React.Dispatch<React.SetStateAction<string | null>>,
    setGenerationStatus: React.Dispatch<React.SetStateAction<string | null>>,
    setPostProcessingFailed: React.Dispatch<React.SetStateAction<boolean>>,
    chatSessionId?: number,
    commitId?: string,
    processedImage?: Blob | null,
    processedDataToon?: string | null,
    processedDataFileName?: string | null
) => {
    try {
        if (chatSessionId && !commitId) {
            const error = new Error('We are experiencing a temporary issue. Please try again in 2 minutes.');
            (error as any).code = 'DDMPC001';
            (error as any).details = 'Project context lost - chatSessionId exists but no commitId in handleGenerate';
            console.error('[DDMPC001] Context lost: chatSessionId exists but no commitId');
            throw error;
        }

        // Follow up prompts
        if (commitId && chatSessionId) {
            const payload = await createPayload({
                chatSessionId,
                userEmail,
                sessionId,
                prompt,
                designSystem,
                templateVersion,
                attachFile,
                attachDataFile,
                processedImage,
                processedDataToon,
                processedDataFileName,
                commitId,
                modificationOptions,
                setCurrentChatSessionId,
            });
            const successfulResponses: any[] = [];

            getChatMessages(payload)
                .then(async (response: any) => {
                    const count = 1;
                    if (response !== null) {
                        await handleSuccessResponse(
                            response,
                            `${projectCount - count + 1}`,
                            setStreamMessageProvider,
                            successfulResponses,
                            setCurrentSource,
                            currentGenerationId,
                            setCurrentGenerationId,
                            setIsGenerateComplete,
                            setShowCodeStream,
                            setStreamMessagesConsumer,
                            sessionId,
                            userEmail,
                            setCurrentChatSessionId,
                            setHistory,
                            setThread,
                            setCurrentHistoryCommit,
                            false,
                            setToolStatus,
                            setGenerationStatus,
                            setPostProcessingFailed
                        );
                    }
                })
                .catch((error) => {
                    if (error instanceof DOMException && error.name === 'AbortError') {
                        console.info('[GenerateHandler] Follow-up stream aborted by user');
                        setIsGenerateComplete(true);
                        setShowCodeStream(false);
                        setToolStatus(null);
                        setGenerationStatus(null);
                        setStreamMessagesConsumer([]);
                        setStreamMessageProvider([]);
                        return;
                    }

                    console.error('[GenerateHandler] Follow-up chat message streaming failed:', error);

                    setGenerationError({
                        message: error.message?.includes('Session expired')
                            ? 'Your session has expired. Redirecting to login...'
                            : 'Unable to generate code. Please try again.',
                        error: error,
                        prompt: prompt,
                        timestamp: Date.now(),
                    });

                    // Clean up streaming state
                    setIsGenerateComplete(true);
                    setShowCodeStream(false);
                    setToolStatus(null);
                    setGenerationStatus(null);
                    setStreamMessagesConsumer([]);
                    setStreamMessageProvider([]);
                });
        } else {
            const successfulResponses: any[] = [];
            const payload = await createPayload({
                chatSessionId,
                userEmail,
                sessionId,
                prompt,
                designSystem,
                templateVersion,
                attachFile,
                attachDataFile,
                processedImage,
                processedDataToon,
                processedDataFileName,
                commitId,
                modificationOptions,
                setCurrentChatSessionId,
            });

            getChatMessages(payload)
                .then(async (response: any) => {
                    if (response !== null) {
                        await handleSuccessResponse(
                            response,
                            '1',
                            setStreamMessageProvider,
                            successfulResponses,
                            setCurrentSource,
                            currentGenerationId,
                            setCurrentGenerationId,
                            setIsGenerateComplete,
                            setShowCodeStream,
                            setStreamMessagesConsumer,
                            sessionId,
                            userEmail,
                            setCurrentChatSessionId,
                            setHistory,
                            setThread,
                            setCurrentHistoryCommit,
                            true,
                            setToolStatus,
                            setGenerationStatus,
                            setPostProcessingFailed
                        );
                    }
                })
                .catch((error) => {
                    if (error instanceof DOMException && error.name === 'AbortError') {
                        console.info('[GenerateHandler] Initial stream aborted by user');
                        setIsGenerateComplete(true);
                        setShowCodeStream(false);
                        setToolStatus(null);
                        setGenerationStatus(null);
                        setStreamMessagesConsumer([]);
                        setStreamMessageProvider([]);
                        return;
                    }

                    console.error('[GenerateHandler] Initial chat message streaming failed:', error);

                    // Set error state for user notification
                    setGenerationError({
                        message: error.message?.includes('Session expired')
                            ? 'Your session has expired. Redirecting to login...'
                            : 'Unable to generate code. Please try again.',
                        error: error,
                        prompt: prompt,
                        timestamp: Date.now(),
                    });

                    // Clean up streaming state
                    setIsGenerateComplete(true);
                    setShowCodeStream(false);
                    setToolStatus(null);
                    setGenerationStatus(null);
                    setStreamMessagesConsumer([]);
                    setStreamMessageProvider([]);
                });
        }
    } catch (error) {
        console.error('Error fetching data from API: ', error);
    }
};
